import { fetchLoginCall } from "../ajax";
import { API_CONSTANTS, API_METHODS } from "../api-constant";
import {
  QUESTION_TYPES,
  CHOICE_QUESTION_TYPES,
  STRUCTURAL_QUESTION_TYPES,
} from "../surveysConstants";
import { downloadBase64File } from "../downloadFile";

// Real backend for survey create/edit/publish/archive, list, detail,
// assignment, results, submission detail, and the survey dropdown
// (SaveSurvey, GetAllSurveys, GetSurveyById, AssignSurvey, GetSurveyAssignments,
// DownloadSurveyJson, GetSurveySubmissionById, GetAllSurveyIds).

const SAVE_ACTION = { DRAFT: 1, PUBLISH: 2, ARCHIVE: 3 };

// Maps this app's internal question-builder types to the "components[].type"
// values inside surveyJson. Only section and single_choice->radio are
// confirmed against a real sample payload; the rest are a best-effort guess.
// Nothing outside this file's own save/load round-trip depends on these
// exact strings, so if the backend expects different values, only this map
// needs to change.
const COMPONENT_TYPE_MAP = {
  [QUESTION_TYPES.SECTION]: "section",
  [QUESTION_TYPES.INSTRUCTION]: "instruction",
  [QUESTION_TYPES.SHORT_TEXT]: "text",
  [QUESTION_TYPES.LONG_TEXT]: "textarea",
  [QUESTION_TYPES.YES_NO]: "yesno",
  [QUESTION_TYPES.DROPDOWN]: "dropdown",
  [QUESTION_TYPES.SINGLE_CHOICE]: "radio",
  [QUESTION_TYPES.MULTI_CHOICE]: "checkbox",
  [QUESTION_TYPES.RATING]: "rating",
  [QUESTION_TYPES.NUMERIC]: "number",
};
const QUESTION_TYPE_FROM_COMPONENT = Object.fromEntries(
  Object.entries(COMPONENT_TYPE_MAP).map(([questionType, componentType]) => [
    componentType,
    questionType,
  ]),
);

const questionsToComponents = (questions = []) =>
  questions.map((q, index) => ({
    order: index + 1,
    type: COMPONENT_TYPE_MAP[q.type] || q.type,
    text: q.label || "",
    help_text: q.helpText || undefined,
    required: !!q.required,
    options: CHOICE_QUESTION_TYPES.includes(q.type)
      ? (q.options || []).map((text, i) => ({ order: i + 1, text }))
      : undefined,
    scale_min: q.type === QUESTION_TYPES.RATING ? q.scaleMin : undefined,
    scale_max: q.type === QUESTION_TYPES.RATING ? q.scaleMax : undefined,
  }));

const componentsToQuestions = (components = []) =>
  components.map((c, index) => ({
    id: `Q-${index}-${Math.random().toString(36).slice(2, 6)}`,
    type: QUESTION_TYPE_FROM_COMPONENT[c.type] || c.type,
    label: c.text || "",
    helpText: c.help_text || "",
    required: !!c.required,
    options: (c.options || [])
      .toSorted((a, b) => a.order - b.order)
      .map((o) => o.text),
    scaleMin: c.scale_min,
    scaleMax: c.scale_max,
  }));

// study/clinical-purpose metadata isn't part of the documented SaveSurvey
// contract (only surveyTitle/description are top-level), so it rides inside
// the surveyJson blob this app defines, sends, and reads back.
const buildSurveyJson = (formData) =>
  JSON.stringify({
    study_or_program: formData.studyName || "",
    clinical_purpose: formData.clinicalPurpose || "",
    participant_instructions: formData.description || "",
    components: questionsToComponents(formData.questions),
  });

const parseSurveyJson = (surveyJson) => {
  if (!surveyJson) return { studyName: "", clinicalPurpose: "", questions: [] };
  try {
    const parsed = JSON.parse(surveyJson);
    return {
      studyName: parsed.study_or_program || "",
      clinicalPurpose: parsed.clinical_purpose || "",
      questions: componentsToQuestions(parsed.components),
    };
  } catch {
    return { studyName: "", clinicalPurpose: "", questions: [] };
  }
};

// responseJson confirmed against the mobile team's integration doc:
// {"responses":[{"questionId":"Q001","question":"...","type":"radio","answer":"No"}, ...]}
// The mobile app assigns questionId itself (sequential per rendered question)
// rather than echoing anything from our surveyJson — and our own surveyJson
// components carry no stable id to match against (componentsToQuestions
// generates a fresh random `id` on every parse). So matching by array
// position is the only option, not a fallback: the mobile app renders
// components in the same answerable-question order it received them in, so
// position order should always agree between surveyJson and responseJson.
const parseResponseJson = (surveyJson, responseJson) => {
  const answerable = parseSurveyJson(surveyJson).questions.filter(
    (q) => !STRUCTURAL_QUESTION_TYPES.includes(q.type),
  );
  let responses = [];
  try {
    const parsed =
      typeof responseJson === "string"
        ? JSON.parse(responseJson)
        : responseJson;
    responses = Array.isArray(parsed) ? parsed : parsed?.responses || [];
  } catch {
    responses = [];
  }
  return answerable.map((q, index) => {
    const match = responses[index];
    return {
      questionId: q.id,
      questionText: q.label,
      type: match?.type,
      answer: match?.answer ?? "",
    };
  });
};

// Fallback only for when statusName is missing from the response — statusName
// (when present) is the source of truth.
const STATUS_NAME_BY_CODE = { 1: "draft", 2: "published", 3: "archived" };
const normalizeStatus = (raw) =>
  (raw.statusName || STATUS_NAME_BY_CODE[raw.status] || "draft").toLowerCase();

// GetAllSurveys' status filter: 0 = all, 1 = draft, 2 = published, 3 = archived.
const STATUS_CODE_BY_NAME = { draft: 1, published: 2, archived: 3 };
const toStatusFilterCode = (status) => STATUS_CODE_BY_NAME[status] || 0;

const mapSurveyListItem = (raw) => ({
  id: raw.surveyId,
  surveyGroupId: raw.surveyGroupId,
  title: raw.surveyTitle,
  description: raw.description,
  version: raw.version,
  status: normalizeStatus(raw),
  createdAt: raw.createdDate,
  updatedAt: raw.updatedDate,
  publishedAt: raw.publishedDate,
});

export const GetSurveyList = async ({
  pageIndex = 0,
  pageSize = 10,
  searchString = "",
  status = "",
} = {}) => {
  const res = await fetchLoginCall(
    API_CONSTANTS.GET_ALL_SURVEYS,
    API_METHODS.POST,
    {
      pageIndex,
      pageSize,
      searchString,
      status: toStatusFilterCode(status),
    },
  );
  if (res.statusCode !== 200) return res;
  return { ...res, data: (res.data || []).map(mapSurveyListItem) };
};

export const GetSurveyById = async (surveyId) => {
  const res = await fetchLoginCall(
    `${API_CONSTANTS.GET_SURVEY_BY_ID}?surveyId=${surveyId}`,
    API_METHODS.GET,
  );
  if (res.statusCode !== 200) return res;
  const raw = res.data;
  return {
    ...res,
    data: {
      id: raw.surveyId,
      surveyGroupId: raw.surveyGroupId,
      title: raw.surveyTitle,
      description: raw.description,
      version: raw.version,
      status: normalizeStatus(raw),
      publishedAt: raw.publishedDate,
      ...parseSurveyJson(raw.surveyJson),
    },
  };
};

const saveSurvey = (surveyId, action, formData) =>
  fetchLoginCall(API_CONSTANTS.SAVE_SURVEY, API_METHODS.POST, {
    surveyId: surveyId || null,
    action,
    surveyTitle: formData?.title || "",
    description: formData?.description || "",
    surveyJson: formData ? buildSurveyJson(formData) : "",
  });

// Creates a new draft, updates an existing draft, or (per the API) silently
// creates a new draft version if surveyId already points at a published survey.
export const SaveSurveyDraft = (surveyId, formData) =>
  saveSurvey(surveyId, SAVE_ACTION.DRAFT, formData);
export const PublishSurvey = (surveyId) =>
  saveSurvey(surveyId, SAVE_ACTION.PUBLISH, null);
// Archiving revokes all active patient assignments automatically, server-side.
export const ArchiveSurvey = (surveyId) =>
  saveSurvey(surveyId, SAVE_ACTION.ARCHIVE, null);

export const AssignSurveyToPatient = async ({ surveyId, patientIds }) =>
  fetchLoginCall(API_CONSTANTS.ASSIGN_SURVEY, API_METHODS.POST, {
    surveyId,
    patientIds,
  });

export const GetSurveyAssignments = async ({
  surveyId = "",
  searchString = "",
  status = 0,
  pageIndex = 0,
  pageSize = 10,
} = {}) => {
  const res = await fetchLoginCall(
    API_CONSTANTS.GET_SURVEY_ASSIGNMENTS,
    API_METHODS.POST,
    {
      surveyId: surveyId || null,
      searchString,
      status: status || 0,
      pageIndex,
      pageSize,
    },
  );
  if (res.statusCode !== 200) return res;
  return {
    ...res,
    data: (res.data || []).map((r) => ({
      id: r.patientSurveyId,
      patientId: r.patientId,
      patientName: r.patientName,
      assignedAt: r.assignedDate,
      submittedAt: r.submittedDate,
      status: r.status,
      statusName: r.statusName,
    })),
  };
};

export const RevokeSurveyAssignment = async (patientSurveyIds) =>
  fetchLoginCall(API_CONSTANTS.REVOKE_SURVEY_ASSIGNMENT, API_METHODS.POST, {
    patientSurveyIds,
  });

// One patientSurveyId comes back as a single JSON file; more than one comes
// back as a ZIP of JSON files — either way the API hands back base64 content
// plus its own fileName/contentType, so decoding is all this needs to do.
export const DownloadSurveyJson = async (patientSurveyIds) => {
  const res = await fetchLoginCall(
    API_CONSTANTS.DOWNLOAD_SURVEY_JSON,
    API_METHODS.POST,
    { patientSurveyIds },
  );
  if (res.statusCode === 200 && res.data) {
    downloadBase64File(
      res.data.base64Content,
      res.data.fileName,
      res.data.contentType,
    );
  }
  return res;
};

// One patient's full survey assignment history, across all surveys —
// used by the Patient Details "Survey" tab (as opposed to
// GetSurveyAssignments, which is scoped to one survey across all patients).
export const GetSurveyHistoryByPatientId = async (patientId) => {
  const res = await fetchLoginCall(
    `${API_CONSTANTS.GET_SURVEY_HISTORY_BY_PATIENT_ID}?patientId=${patientId}`,
    API_METHODS.GET,
  );
  if (res.statusCode !== 200) return res;
  return {
    ...res,
    data: (res.data || []).map((r) => ({
      id: r.patientSurveyId,
      surveyId: r.surveyId,
      surveyTitle: r.surveyTitle,
      surveyVersion: r.surveyVersion,
      assignedAt: r.assignedDate,
      completedAt: r.completedDate,
      revokedAt: r.revokedDate,
      status: r.status,
      statusName: r.statusName,
    })),
  };
};

export const GetSurveySubmissionById = async (patientSurveyId) => {
  const res = await fetchLoginCall(
    `${API_CONSTANTS.GET_SURVEY_SUBMISSION_BY_ID}?patientSurveyId=${patientSurveyId}`,
    API_METHODS.GET,
  );
  if (res.statusCode !== 200) return res;
  const raw = res.data;
  return {
    ...res,
    data: {
      id: raw.patientSurveyId,
      surveyTitle: raw.surveyTitle,
      patientName: raw.patientName,
      surveyVersion: raw.surveyVersion,
      assignedAt: raw.assignedDate,
      submittedAt: raw.submittedDate,
      ...parseSurveyJson(raw.surveyJson),
      answers: parseResponseJson(raw.surveyJson, raw.responseJson),
    },
  };
};
